import Axios from "axios";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../utils/apiMessageHelper";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip as ChartTooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import { Bar } from "react-chartjs-2";
import Dropdown from "react-dropdown";
import * as XLSX from "xlsx";
import { DataTable } from "./utils/dataTable";
import ClipLoader from "react-spinners/ClipLoader";
import { DropDownClients } from "./clients/dropDownClients";
import { SideBar } from "./sideBar";
import { NavBar } from "./navBar";
// Contexts
import { useNotification } from "../contexts/NotificationContext";
import { useAuth } from "../contexts/AuthContext";
// Enums
import { reportOptions } from "../enums/reportEnums.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ChartDataLabels,
);

ChartJS.defaults.set("plugins.datalabels", {
  color: "#000000",
});

export const MainReports = () => {
  const { t } = useTranslation();
  const { clientId } = useAuth();
  const { notify } = useNotification();

  // Show sidebar if user has clientId (both Super Admin and Client Admin)
  const hasPermissions = clientId !== null && clientId !== undefined;

  // Check if user is Super Admin (clientId === 1)
  const isSuperAdmin = clientId === 1;

  //> States
  // Obtener fecha local en formato YYYY-MM-DD
  const getLocalDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(getLocalDateString());
  const [endDate, setEndDate] = useState(getLocalDateString());
  const [reportOption, setReportOption] = useState(reportOptions[0]);
  const [reportData, setReportData] = useState({
    report: [],
    reportDetail: [],
    reportType: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [reportUserOptions, setReportUserOptions] = useState([]);
  const [reportDepartmentOptions, setReportDepartmentOptions] = useState([]);
  const [selectedReportEntityValues, setSelectedReportEntityValues] = useState(
    [],
  );
  const [selectAllEntities, setSelectAllEntities] = useState(false);

  // Selected client for Super Admin (default to their own clientId for Client Admin)
  const [selectedClientId, setSelectedClientId] = useState(
    isSuperAdmin ? null : clientId,
  );

  useEffect(() => {
    setSelectedReportEntityValues([]);
    setSelectAllEntities(false);
  }, [reportOption]);

  //> Handlers
  const handleReportOptionChange = (e) => {
    setReportOption(e);
  };

  const handleReportUserChange = (e) => {
    setSelectedReportEntityValues(Array.isArray(e) ? e.map((x) => x) : []);
  };

  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked;
    setSelectAllEntities(isChecked);

    // Limpiar selección cuando se marca "Todos"
    if (isChecked) {
      setSelectedReportEntityValues([]);
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date.target.value);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date.target.value);
  };

  const exportToExcel = () => {
    const dateRange = `${startDate}_${endDate}`;
    const reportType = reportOption.value;
    const reportTitle =
      reportOptions.find((option) => option.value === reportType)?.label ||
      "Unknown Report";
    const ws = XLSX.utils.json_to_sheet(reportData.reportDetail);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report Data");
    XLSX.writeFile(wb, reportTitle + " " + dateRange + ".xlsx");
  };

  //> API Calls
  async function getUsers() {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/User/list/${selectedClientId}`,
      );

      setReportUserOptions(
        res.data.responseObject.map((user) => {
          return {
            value: user.idUser,
            label: user.firstName + " " + user.lastName,
          };
        }),
      );
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    } finally {
      getDepartments();
    }
  }

  async function getDepartments() {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/Department/list/${selectedClientId}`,
      );

      setReportDepartmentOptions(
        res.data.responseObject.map((department) => {
          return {
            value: department.idDepartment,
            label: department.name,
          };
        }),
      );
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    }
  }

  const getReportData = async () => {
    setIsLoading(true);

    let entityIds;

    // Si "Todos" está seleccionado, usar todos los IDs disponibles
    if (selectAllEntities) {
      const allOptions = reportOption.value === 0 ? reportUserOptions : reportDepartmentOptions;
      entityIds = allOptions
        .map((option) => encodeURIComponent(option.value))
        .join("%2C");
    } else {
      // Usar la selección específica del usuario
      entityIds = selectedReportEntityValues
        .map((user) => encodeURIComponent(user.value))
        .join("%2C");
    }

    const idReport = reportOption.value;

    // Construir la URL con startDate y endDate
    const url = `${process.env.REACT_APP_API_URL}/api/v1/Report/${idReport}/${entityIds}?startDate=${startDate}&endDate=${endDate}`;

    try {
      const res = await Axios.get(url);

      if (res.status === 200) {
        setReportData(res.data.responseObject);
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClientChange = (selected) => {
    if (selected) {
      // The dropdown now passes the full client object with idClient
      const newClientId = selected.idClient;
      // Allow setting to null when empty option is selected
      setSelectedClientId(newClientId);
      // Clear selected users/departments when client changes
      setSelectedReportEntityValues([]);
      // Clear selectAll checkbox
      setSelectAllEntities(false);
      // Clear report data when client changes or empty option selected
      setReportData({
        report: [],
        reportDetail: [],
        reportType: null,
      });
      // Clear options when empty option is selected
      if (newClientId === null) {
        setReportUserOptions([]);
        setReportDepartmentOptions([]);
      }
    }
  };

  //> Lifecycle
  useEffect(() => {
    if (selectedClientId) {
      getUsers();
    }
  }, [selectedClientId]);

  //> Chart Configuration
  const graphicOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text:
          reportOption.value === 0
            ? t("reports.userActivityReportTitle")
            : t("reports.departmentActivityReportTitle"),
        font: {
          size: 16,
          weight: "bold",
        },
      },
      datalabels: {
        display: true,
        anchor: "end",
        align: "top",
        formatter: (value) => {
          return Math.round(value * 100) / 100;
        },
        font: {
          weight: "bold",
          size: 9,
        },
        color: "#000",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              const value = Math.round(context.parsed.y * 100) / 100;
              label += value;

              // Agregar unidades según el dataset
              if (context.dataset.label === t("reports.completedVideosLabel")) {
                label += " " + t("reports.videosUnit");
              } else if (
                context.dataset.label === t("reports.investedMinutesLabel")
              ) {
                label += " " + t("reports.minutesUnit");
              } else if (
                context.dataset.label === t("reports.investedHoursLabel")
              ) {
                label += " " + t("reports.hoursUnit");
              }
            }
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      x: {
        ticks: {
          font: {
            size: 11,
          },
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  // Mapear datos del reporte (funciona para usuario y departamento)
  const labels = reportData.report.map((item) => {
    // Usar fullName para reportes de usuario, departmentName para reportes de departamento
    return item.fullName || item.departmentName || "Sin nombre";
  });
  const videosCompletados = reportData.report.map(
    (item) => item.totalCompletedVideos || 0,
  );
  const minutosInvertidos = reportData.report.map(
    (item) => item.totalMinutesInvested || 0,
  );
  const horasInvertidas = reportData.report.map(
    (item) => item.totalHoursInvested || 0,
  );

  const graphicData = {
    labels,
    datasets: [
      {
        label: t("reports.completedVideosLabel"),
        data: videosCompletados,
        backgroundColor: "rgba(54, 162, 235, 0.7)", // Azul
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
      {
        label: t("reports.investedMinutesLabel"),
        data: minutosInvertidos,
        backgroundColor: "rgba(255, 159, 64, 0.7)", // Naranja
        borderColor: "rgba(255, 159, 64, 1)",
        borderWidth: 1,
      },
      {
        label: t("reports.investedHoursLabel"),
        data: horasInvertidas,
        backgroundColor: "rgba(75, 192, 192, 0.7)", // Verde agua
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };

  //> Columns
  const reportColumns = [
    {
      Header: t("reports.tableHeaderDepartment"),
      accessor: "department",
    },
    {
      Header: t("reports.tableHeaderUser"),
      accessor: "user",
    },
    {
      Header: t("reports.tableHeaderRoutine"),
      accessor: "routine",
    },
    {
      Header: t("reports.tableHeaderVideo"),
      accessor: "video",
      Cell: ({ value }) =>
        value === null ? t("reports.notAvailableText") : value,
    },
    {
      Header: t("reports.tableHeaderCompleted"),
      accessor: "completed",
      Cell: ({ value }) =>
        value === null ? t("reports.notAvailableText") : value,
    },
  ];

  return (
    <React.Fragment>
      {hasPermissions && <SideBar />}
      <Container>
        <NavBar />
        <Container>
          <Row>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <Card.Title as="h5">{t("reports.pageTitle")}</Card.Title>
                  {isSuperAdmin && (
                    <div style={{ minWidth: "250px" }}>
                      <DropDownClients
                        value={selectedClientId}
                        onChange={handleClientChange}
                        className="form-control"
                      />
                    </div>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <div>
                  <Row>
                    <Col xs={12}>
                      {/* Report Date */}
                      <div className="row m-t-30">
                        <div className="col-2">
                          <span>{t("reports.startDateLabel")}</span>
                        </div>
                        <div className="col-4">
                          <Form.Control
                            required
                            minLength={5}
                            autoFocus
                            autoComplete="off"
                            type="date"
                            value={startDate}
                            name="startDate"
                            placeholder="Fecha Inicio"
                            onChange={handleStartDateChange}
                          />
                        </div>
                        <div className="col-2">
                          <span>{t("reports.endDateLabel")}</span>
                        </div>
                        <div className="col-4">
                          <Form.Control
                            required
                            minLength={5}
                            autoComplete="off"
                            type="date"
                            value={endDate}
                            name="endDate"
                            placeholder="Fecha Fin"
                            onChange={handleEndDateChange}
                          />
                        </div>
                      </div>
                      <div className="row m-t-20">
                        <div className="col-2">
                          <span>
                            {reportOption.value === 0
                              ? t("reports.usersLabel")
                              : t("reports.departmentsLabel")}
                          </span>
                        </div>
                        <div className="col-8">
                          <Select
                            isMulti
                            name="reportUsers"
                            value={selectedReportEntityValues}
                            options={
                              reportOption.value === 0
                                ? reportUserOptions
                                : reportDepartmentOptions
                            }
                            className="basic-multi-select"
                            classNamePrefix="select"
                            onChange={handleReportUserChange}
                            isDisabled={selectAllEntities}
                          />
                        </div>
                        <div className="col-2 d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            id="selectAllEntitiesCheckbox"
                            label={t("reports.allOption")}
                            checked={selectAllEntities}
                            onChange={handleSelectAllChange}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: 20, marginBottom: 10 }}>
                    <Col xs={12}>
                      <Dropdown
                        options={reportOptions}
                        value={reportOption}
                        onChange={handleReportOptionChange}
                        placeholder={t("reports.selectReportPlaceholder")}
                      />
                    </Col>
                  </Row>
                  <div id="flexDiv">
                    <Button
                      id="btnLarge"
                      variant="primary"
                      disabled={
                        (selectedReportEntityValues.length === 0 && !selectAllEntities) ||
                        !startDate ||
                        !endDate
                      }
                      onClick={getReportData}
                    >
                      {t("reports.generateReportButton")}
                    </Button>
                    <Button
                      id="btnLarge"
                      variant="success"
                      onClick={exportToExcel}
                      disabled={reportData.reportDetail.length === 0}
                    >
                      {t("reports.exportToExcelButton")}
                    </Button>
                  </div>
                </div>

                {isLoading ? (
                  <div className="load-icon">
                    <ClipLoader
                      size={50}
                      color={"#0b5ed7"}
                      loading={isLoading}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      overflowY: "auto",
                      gap: 20,
                    }}
                  >
                    <div style={{ height: "400px", marginBottom: "20px" }}>
                      <Bar options={graphicOptions} data={graphicData} />
                    </div>
                    <DataTable
                      columns={reportColumns}
                      data={reportData.reportDetail}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Row>
        </Container>
      </Container>
    </React.Fragment>
  );
};
